import React, { useState } from "react";
import { Form, Input, Modal, Switch, message } from "antd";
import { useChat } from "./ChatContext";
import UserPicker from "./UserPicker";
import { CHAT_MODAL_Z_INDEX } from "./types";
import { logZulipChannelCreated } from "@/services/app/tenant/integration/zulipChannels";

const NewChannelModal: React.FC = () => {
  const { newChannelOpen, setNewChannelOpen, createChannel, selectChannel } = useChat();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const created = await createChannel({
        name: values.name.trim(),
        description: values.description,
        inviteOnly: !!values.inviteOnly,
        principals: values.members ?? [],
      });
      message.success(`#${values.name} created`);
      setNewChannelOpen(false);
      form.resetFields();
      if (created) {
        // Best-effort audit log — createChannel() talks to Zulip directly
        // with the member's own credentials, bypassing our backend entirely.
        logZulipChannelCreated(created.stream_id, { name: values.name.trim(), invite_only: !!values.inviteOnly });
        selectChannel(created, "");
      }
    } catch (e: any) {
      if (e?.errorFields) return; // form validation error — already shown inline
      message.error(e?.message || "Failed to create channel.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="New channel"
      open={newChannelOpen}
      onOk={handleOk}
      onCancel={() => setNewChannelOpen(false)}
      confirmLoading={submitting}
      okText="Create"
      destroyOnClose
      zIndex={CHAT_MODAL_Z_INDEX}
    >
      <Form form={form} layout="vertical" initialValues={{ inviteOnly: false }}>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: "Channel name is required" }]}>
          <Input placeholder="e.g. field-team" maxLength={60} />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} maxLength={200} />
        </Form.Item>
        <Form.Item name="inviteOnly" label="Private channel" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="members" label="Add members">
          <UserPicker placeholder="Search people to add…" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NewChannelModal;
